/**
 * Who the student is talking to, and what has been said.
 *
 * An external store in the house dialect (subscribe returning an
 * unsubscribe, module-scoped state) because it is driven from the 3D click
 * and the frame loop and read by React. Opening a conversation turns the
 * technician (through the scene hooks `init` wires), takes the walk-lock the
 * way a tool panel does - the keyboard belongs to the text box while a chat
 * is up - and seeds the transcript with a greeting the brief can back.
 *
 * Every line the student sends rebuilds the brief from the live scene, so a
 * tool that alarms mid-conversation is reported as alarming.
 */

import { castById, castName, type CastMember } from '../fab/cast'
import { getTool } from '../fab/layout'
import { talkLog } from './talkLog'
import { EXPLAIN, UNPROMPTED } from './wire'
import { acquireWalkLock } from '../app/input/walkLock'
import { t, tOr } from '../i18n/i18n'
import type { TalkBrief } from './brief'
import { askTechnician, type TalkTurn } from './talkClient'
import { runExperiment, warmExperiments } from './experimentRunner'
import type { AskProgress } from '../assistant/Assistant'
import { measured, PREDICTION_MAX } from './predict'
import type { ExperimentReport } from '../assistant/types'
import { canSpeak, speak, stopSpeaking } from './voice'
import { setSoundDucked } from '../fab/sound'

export interface TalkHooks {
  engage(id: string): void
  disengage(): void
  setTalking(on: boolean): void
  brief(id: string): TalkBrief | null
  /**
   * A prediction the student made, beside what the tool then measured.
   *
   * A hook rather than an import so this store stays clear of the learn
   * layer, the same way `brief` is one. Called once, after the report
   * lands, because a call with nothing to hold it against is not worth
   * keeping - and never with a verdict, because nothing here has one.
   */
  predicted(about: string, said: string, measured: string): void
  /**
   * What they said when she asked why it went that way - the third beat.
   *
   * Separate from `predicted` because it arrives one turn later and is
   * answered rather than consumed: an explanation is a real line in the
   * conversation and gets a reply, where a prediction is swallowed by the
   * hold. Never with a verdict, for the same reason as the call.
   */
  explained(about: string, said: string): void
}

const VOICE_KEY = 'simfab.talk.voice'

/**
 * The questions this student has asked, oldest first - the up-arrow
 * history in the ask box.
 *
 * It lives here rather than in the panel because the panel unmounts the
 * moment a conversation ends, and the whole point is that a question you
 * asked Sarah can be recalled at Emily without retyping it. One list
 * across every technician, the way a shell has one history across every
 * directory.
 *
 * Persisted, with the same expiry `place.ts` uses and for the same reason:
 * a shared lab machine should not hand next week's student the last one's
 * questions. Every read and write is guarded - a private window that
 * refuses storage gets working history for the session and nothing kept.
 */
const HISTORY_KEY = 'simfab.talk.history'
const HISTORY_MAX = 50
const HISTORY_MAX_AGE_MS = 14 * 86_400_000

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const stored = JSON.parse(raw) as { savedAt?: number; lines?: unknown }
    if (typeof stored.savedAt !== 'number' || Date.now() - stored.savedAt > HISTORY_MAX_AGE_MS) {
      localStorage.removeItem(HISTORY_KEY)
      return []
    }
    return Array.isArray(stored.lines) ? stored.lines.filter((l): l is string => typeof l === 'string') : []
  } catch {
    return []
  }
}

function saveHistory(lines: string[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify({ savedAt: Date.now(), lines }))
  } catch {
    // History that cannot be kept is a retype, not a failure.
  }
}

let hooks: TalkHooks | null = null
let open: CastMember | null = null
let transcript: TalkTurn[] = []
let busy = false
/** How far through an experiment the tool is, while one is running. */
let progress: AskProgress | null = null
let walkLock: (() => void) | null = null
let mouthTimer: ReturnType<typeof setTimeout> | null = null
/** The reply in flight, so Escape can call it off. */
let pending: AbortController | null = null

/**
 * A prediction the technician has asked for, with her tool already working.
 *
 * The whole of the mechanism is here: while this is set the ask box belongs
 * to the STUDENT even though the conversation is mid-turn, and the report
 * the solver has already produced waits on `go`. See predict.ts for why the
 * hold is the point and why nothing grades what they say.
 */
interface Awaiting {
  who: CastMember
  /** What she asked them to call, in her words. Shown, and journalled. */
  about: string
  /** What they called it, once they call it. Empty if they skipped. */
  said: string
  /** Releases the report. */
  go: () => void
}
let awaiting: Awaiting | null = null
/**
 * How long a report waits on a student who is not going to answer.
 *
 * They can skip, and closing the panel releases it too - this is only for
 * the student who walked away from the keyboard without doing either. A
 * report that never arrives is a worse outcome than one that arrives with
 * nothing paired to it.
 */
const PREDICT_WAIT_MS = 90_000
let predictTimer: ReturnType<typeof setTimeout> | null = null

/**
 * The call she has just asked them to explain, if she has.
 *
 * The next line the student types is their answer to it. Unlike a prediction
 * this is NOT intercepted - an explanation is a real turn that gets a reply -
 * so all this does is see it go past on its way to the model and put a copy
 * in the record.
 *
 * Exactly one line: if they change the subject instead, that IS what they
 * said when asked, and the record keeps the utterance rather than a judgement
 * about whether it answered. Every label on it says "when asked why".
 */
let asked: { about: string } | null = null

/** What `execute` waits on before it shows the report, or null for no wait. */
let held: Promise<void> | null = null

/**
 * The last report this technician's tool produced in the conversation on
 * screen, so the brief can carry what it said.
 *
 * The transcript holds the report object, but what goes over the wire is
 * `{ role, text }` and a report turn's text is `report.understood` - the
 * question, not the answer. Without this she runs an experiment on her own
 * tool, watches the result appear between them, and cannot see it.
 *
 * Dies with the conversation, like the transcript it belongs to.
 */
let reported: ExperimentReport | null = null

/**
 * Start holding the report back, and open the box for their answer.
 *
 * The run is ALREADY in flight when this is called: the tool works while the
 * student thinks, so the pause costs the conversation nothing it was not
 * spending anyway.
 */
function hold(who: CastMember, about: string): void {
  let go = (): void => {}
  held = new Promise<void>((resolve) => {
    go = resolve
  })
  awaiting = { who, about, said: '', go }
  predictTimer = setTimeout(release, PREDICT_WAIT_MS)
}

/** Let the report through, however it was let through. */
function release(): void {
  if (predictTimer) clearTimeout(predictTimer)
  predictTimer = null
  awaiting?.go()
}

/**
 * Wait for the student to call it, and hand back what they said - or null
 * where nothing was being held, or where they let it go without answering.
 * Clears the hold either way: one report, one wait.
 */
async function settle(): Promise<{ about: string; said: string } | null> {
  if (!held) return null
  await held
  held = null
  const was = awaiting
  awaiting = null
  notify()
  return was?.said ? { about: was.about, said: was.said } : null
}
/** Whether a line is being delivered right now - spoken, or its clip running. */
let speaking = false
/** Whether replies are read aloud. Remembered across sessions. */
let voiceOn = false
try {
  voiceOn = localStorage.getItem(VOICE_KEY) === 'on'
} catch {
  // A private window that refuses storage still gets a working panel.
}
let history: string[] = loadHistory()
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

/**
 * Say a line, and hold the talking clip while it is being said.
 *
 * With the voice on, the clip is driven by the actual audio - the person
 * stops gesturing when the sentence stops. With it off there is nothing to
 * time against, so the clip runs for about as long as the line takes to
 * read, which is what the panel did before there was any voice at all.
 *
 * `extra` is said but not shown - a scene's own two instruction lines,
 * which the card already prints. Kept out of `text` on purpose: the
 * transcript is what the technician SAID in their own words, and pasting
 * two catalogue sentences into it would put words in their mouth that the
 * student can already read six inches lower.
 */
function perform(text: string, body: 'f' | 'm', extra = ''): void {
  const spoken = extra ? `${text} ${extra}` : text
  hooks?.setTalking(true)
  speaking = true
  if (mouthTimer) clearTimeout(mouthTimer)
  mouthTimer = null
  const done = (): void => {
    hooks?.setTalking(false)
    // The hall comes back up the moment the voice stops.
    setSoundDucked(false)
    mouthTimer = null
    speaking = false
  }
  if (voiceOn && canSpeak()) {
    // Only for a voice there is something to hear over; a silent
    // technician has no reason to quiet the fab around them.
    setSoundDucked(true)
    speak(spoken, body, done)
    // A voice that never fires its end event (a tab backgrounded
    // mid-sentence) must not leave the technician gesturing forever.
    mouthTimer = setTimeout(done, Math.min(30_000, 2000 + spoken.length * 90))
    return
  }
  mouthTimer = setTimeout(done, Math.min(9000, Math.max(1500, spoken.length * 45)))
}

/**
 * A scene's own instructions, as one spoken run: what to watch for, then
 * which control to reach for.
 *
 * Read out only where the technician actually put a scene up. `tOr` with an
 * empty fallback so a scene that has somehow lost a catalogue line goes
 * quiet rather than speaking its own key - `npm run talk` is what stops
 * that happening in the first place.
 */
function spokenDemoLines(id: string): string {
  return [tOr(`talk.demo.${id}.Watch`, ''), tOr(`talk.demo.${id}.Try`, '')]
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ')
}

/**
 * Run an experiment on this technician's tool and put the measured report
 * into the conversation.
 *
 * A placeholder turn goes in FIRST, marked `running`, because this takes
 * seconds of real solver time and a chat that sat silent through them would
 * read as broken. The same turn is then replaced by the report, so the
 * student watches one thing become another rather than two things appear.
 *
 * Nothing here writes a finding. `runExperiment` hands the question to the
 * tool's own assistant, which routes it to a registered intent, executes it
 * against the solver and returns a report assembled from what came back -
 * the same report, from the same assistant, that the tool's panel shows.
 */
async function execute(who: CastMember, question: string): Promise<{ about: string; said: string } | null> {
  if (!who.toolId) return null
  // Held by identity rather than by index: with a prediction pending the
  // student can push a turn in while this one is up, so where it sits is no
  // longer where it was put.
  const placeholder: TalkTurn = { role: 'technician', text: t('talk.experiment.Running'), running: true }
  transcript = [...transcript, placeholder]
  progress = null
  notify()
  const replace = (turn: TalkTurn): void => {
    // The student may have walked off mid-run.
    if (open !== who) return
    const i = transcript.indexOf(placeholder)
    transcript = i < 0 ? [...transcript, turn] : [...transcript.slice(0, i), turn, ...transcript.slice(i + 1)]
    progress = null
    notify()
  }
  try {
    const result = await runExperiment(who.toolId, question, (p) => {
      if (open !== who) return
      progress = p
      notify()
    })
    // The tool has finished. If she asked them to call it first, the report
    // waits here - the solver worked while they thought, so the hold costs
    // the conversation nothing it was not already spending.
    const said = await settle()
    if (open !== who) return null
    if (!result) {
      replace({ role: 'technician', text: t('talk.experiment.Unavailable'), local: true })
      return null
    }
    replace({ role: 'technician', text: result.text, report: result.report })
    // Hers to quote from here on: her tool, her run, this conversation.
    reported = result.report ?? reported
    // Recorded only now, with both halves: what they called, and what the
    // tool measured. A prediction with nothing to hold it against is not
    // worth keeping, so a student who walked off before the report leaves
    // no event behind. Nothing here decides whether they were RIGHT - see
    // predict.ts for why that is deliberate.
    if (said && result.report) hooks?.predicted(said.about, said.said, measured(result.report))
    // The arc is open only where they actually called it: predict, observe,
    // and now the third beat. A run nobody called was a lookup.
    return said && result.report ? said : null
  } catch {
    await settle()
    replace({ role: 'technician', text: t('talk.experiment.Failed'), local: true })
    return null
  }
}

/**
 * The third beat: why did it go that way?
 *
 * Predict, observe, explain. The first two were built and the third was not,
 * so a student could call a result wrong, watch the tool contradict them, and
 * never once be asked what they made of it - which is where a corrected fact
 * becomes a corrected model, or fails to.
 *
 * It costs one model call, and it is spent only where they CALLED the run:
 * she asked, they committed, the tool answered, and the question the answer
 * raises has not been asked by anybody. A run with no call was a lookup and
 * gets nothing.
 *
 * She does not need telling what happened - the brief carries the findings
 * (`reported`) and the call itself (`work`). The instruction only says which
 * moment this is.
 */
async function askWhy(who: CastMember, about: string): Promise<void> {
  if (!hooks || open !== who) return
  const brief = hooks.brief(who.id)
  if (!brief) return
  busy = true
  notify()
  pending = new AbortController()
  const reply = await askTechnician(brief, transcript, EXPLAIN, pending.signal)
  pending = null
  if (open !== who) return
  if (reply) {
    transcript = [...transcript, { role: 'technician', text: reply.text, local: reply.local, demo: reply.demo }]
    // She has asked. The next line is the answer, whatever it turns out to be.
    asked = { about }
    perform(reply.text, who.body, reply.demo ? spokenDemoLines(reply.demo) : '')
  }
  busy = false
  notify()
}


export const talkStore = {
  get open(): CastMember | null {
    return open
  },
  get transcript(): TalkTurn[] {
    return transcript
  },
  get busy(): boolean {
    return busy
  },
  /** How far through an experiment the tool is, or null when none is running. */
  get progress(): AskProgress | null {
    return progress
  },
  /**
   * What her tool reported in this conversation, for the brief. Null until
   * she runs something.
   */
  get lastReport(): ExperimentReport | null {
    return reported
  },
  /**
   * What the technician has asked the student to call before the report
   * lands, or null when nothing is being held. The panel shows it above the
   * box and enables the box on it. See predict.ts.
   */
  get awaiting(): string | null {
    return awaiting?.about ?? null
  },

  /**
   * The student's call. Recorded, shown, and it lets the report through.
   *
   * It goes into the transcript ABOVE the "running" line rather than after
   * it, so the thing still pending stays at the bottom where the eye is and
   * the report replaces it in place.
   */
  predict(said: string): void {
    const call = said.trim().slice(0, PREDICTION_MAX)
    if (!awaiting || awaiting.who !== open || !call) return
    awaiting.said = call
    const mine: TalkTurn = { role: 'student', text: call }
    const i = transcript.findIndex((turn) => turn.running)
    transcript = i < 0 ? [...transcript, mine] : [...transcript.slice(0, i), mine, ...transcript.slice(i)]
    release()
    notify()
  },

  /**
   * "Just show me." A student who does not want to guess gets the report,
   * and nothing is recorded - a call nobody made is not a call. An
   * instructor who would not go on until you answered would not be teaching
   * either.
   */
  skip(): void {
    if (!awaiting) return
    release()
  },
  /** Whether replies are read aloud. */
  get voice(): boolean {
    return voiceOn
  },

  /**
   * Every question asked, oldest first, across all technicians. The ask
   * box walks this with the arrow keys.
   */
  get history(): string[] {
    return history
  },

  setVoice(on: boolean): void {
    if (voiceOn === on) return
    voiceOn = on
    try {
      localStorage.setItem(VOICE_KEY, on ? 'on' : 'off')
    } catch {
      // Not remembering the choice is not a reason to refuse it.
    }
    if (!on) {
      stopSpeaking()
      setSoundDucked(false)
      hooks?.setTalking(false)
      if (mouthTimer) clearTimeout(mouthTimer)
      mouthTimer = null
    }
    notify()
  },

  /**
   * Stop whatever the technician is doing this second, WITHOUT ending the
   * conversation: a reply being fetched, or one being spoken aloud.
   *
   * Returns whether there was anything to stop, which is what lets Escape
   * be one key for both jobs (main.tsx): the first press stops, and a
   * press with nothing to stop leaves. That ladder is what every editor
   * and browser does, and it is why this needs no new key to learn.
   */
  stop(): boolean {
    if (pending) {
      pending.abort()
      pending = null
      return true
    }
    if (speaking) {
      stopSpeaking()
      setSoundDucked(false)
      hooks?.setTalking(false)
      if (mouthTimer) clearTimeout(mouthTimer)
      mouthTimer = null
      speaking = false
      notify()
      return true
    }
    return false
  },

  /** Wired once by the composition root, when the scene exists. */
  init(h: TalkHooks): void {
    hooks = h
  },

  /**
   * `unbidden` says the technician started this, not the student - a knock
   * taken (see `offer`). It changes nothing about the conversation and one
   * thing about the memory of it: being called over is not the student
   * coming to you, so it does not count as a visit. See talkLog.ts.
   */
  openWith(id: string, unbidden = false): void {
    const who = castById(id)
    if (!who || !hooks) return
    if (open?.id === id) return
    open = who
    busy = false
    progress = null
    reported = null
    asked = null
    // Before anything builds a brief: this is the line that says the last
    // conversation is over and everything in it is now memory rather than
    // chat history.
    talkLog.arrive(id, unbidden)
    const brief = hooks.brief(id)
    const greeting = t('talk.local.Greeting', { name: castName(who.id), post: brief?.person.post ?? '' })
    transcript = [{ role: 'technician', text: greeting, local: true }]
    // The catalogue of what this tool can run, fetched while the student
    // types their first line. Nothing is built - see experiments.ts.
    warmExperiments(who.toolId)
    hooks.engage(id)
    walkLock ??= acquireWalkLock()
    perform(greeting, who.body)
    notify()
  },

  close(): void {
    if (!open) return
    open = null
    transcript = []
    busy = false
    progress = null
    // A report held for a call nobody is coming back to make. Let it go so
    // the run that produced it can finish and stop waiting; its own guard
    // drops it, since the person it was for has gone.
    release()
    awaiting = null
    held = null
    reported = null
    asked = null
    // Leaving mid-question calls it off too - the reply has nobody to
    // arrive for, and its own guard would drop it anyway.
    pending?.abort()
    pending = null
    speaking = false
    stopSpeaking()
    setSoundDucked(false)
    if (mouthTimer) clearTimeout(mouthTimer)
    mouthTimer = null
    hooks?.setTalking(false)
    hooks?.disengage()
    walkLock?.()
    walkLock = null
    notify()
  },

  /**
   * A line nobody asked for: the technician speaks first.
   *
   * The one path in this store that is not a reply. It opens the
   * conversation the way a click would - the same greeting, the same
   * walk-lock, the same turned head - and then puts one unprompted line
   * after it, built from the same brief and the same endpoint as every
   * other line. `reason` is the instruction, in English, composed by
   * watch.ts from the journal event that caused it; it is never shown.
   *
   * Nothing here decides WHEN. That is watch.ts's job, and it decides it
   * from the record rather than from an opinion about the student.
   */
  async offer(id: string, reason: string): Promise<void> {
    const who = castById(id)
    if (!who || !hooks || busy) return
    this.openWith(id, true)
    if (open !== who) return
    // NO GREETING. `openWith` seeds one because a student who walked up
    // needs telling who this is - but here she is the one who spoke, and
    // "What do you want to know?" from somebody who wanted something is the
    // wrong sentence. Her own line opens it instead.
    transcript = []
    const brief = hooks.brief(id)
    if (!brief) return
    busy = true
    notify()
    pending = new AbortController()
    // Sent as the student's turn so the wire shape is unchanged, but marked
    // so the prompt knows nobody said it - see UNPROMPTED in talkPrompt.js.
    const reply = await askTechnician(brief, transcript, `${UNPROMPTED} ${reason}`, pending.signal)
    pending = null
    if (open !== who) return
    if (!reply) {
      busy = false
      notify()
      return
    }
    transcript = [...transcript, { role: 'technician', text: reply.text, local: reply.local, demo: reply.demo }]
    // She said it, so she has to remember saying it: a student who walks
    // off and comes back must not be told the same thing again as though it
    // were news. No question on this entry - there was not one.
    talkLog.spoke(who.id, who.toolId ? getTool(who.toolId)?.assetTag ?? '' : '')
    talkLog.settle('answered', reply.text, reply.demo ?? '')
    perform(reply.text, who.body, reply.demo ? spokenDemoLines(reply.demo) : '')
    busy = false
    notify()
  },

  async ask(text: string): Promise<void> {
    const who = open
    const question = text.trim()
    if (!who || !hooks || !question) return
    // She asked them to call it before the tool reports. What they type next
    // is that call, and it is NOT a question: it goes to the transcript and
    // to the journal, and never to the model. Routed here rather than in the
    // panel so a prediction counts however it was entered, the same as
    // everything else that comes through this box.
    if (awaiting) return this.predict(question)
    if (busy) return
    // The answer to "why did it go that way", on its way to the model like
    // any other line. Copied into the record here and not intercepted: this
    // one is a turn she replies to. See `asked` above.
    if (asked) {
      hooks.explained(asked.about, question.slice(0, PREDICTION_MAX))
      asked = null
    }
    // Remembered here rather than in the panel, so a question counts
    // however it was entered - typed, dictated, or recalled and sent again.
    // Consecutive repeats collapse, the way a shell's history does.
    if (history[history.length - 1] !== question) {
      history = [...history, question].slice(-HISTORY_MAX)
      saveHistory(history)
    }
    transcript = [...transcript, { role: 'student', text: question }]
    // The round: what this student has been asking, and of whom, across
    // every technician they have stopped at. Recorded HERE and not on the
    // way out, because the way out has an early return for a student who
    // walked off mid-answer - and a question they abandoned is one of the
    // more interesting things a supervisor could notice. See talkLog.ts.
    talkLog.ask(who.id, who.toolId ? getTool(who.toolId)?.assetTag ?? '' : '', question)
    busy = true
    // Cut a reply the student has spoken over: they asked something else.
    stopSpeaking()
    setSoundDucked(false)
    hooks.setTalking(false)
    notify()
    const brief = hooks.brief(who.id)
    if (!brief) {
      busy = false
      notify()
      return
    }
    pending = new AbortController()
    const reply = await askTechnician(brief, transcript, question, pending.signal)
    pending = null
    // The student may have walked off mid-answer.
    if (open !== who) return
    // Or stopped the question. The question stays in the log - they did
    // ask it - marked as stopped, and the box comes back to them.
    if (!reply) {
      transcript = transcript.map((turn, i) =>
        i === transcript.length - 1 && turn.role === 'student' ? { ...turn, stopped: true } : turn,
      )
      busy = false
      notify()
      return
    }
    // `reply.demo` is a scene id the client already resolved against the
    // brief's own list; the panel mounts it and nothing here re-decides.
    // `reply.text` has had both markers taken out of it, so what is spoken
    // aloud is the sentence and not its punctuation.
    transcript = [...transcript, { role: 'technician', text: reply.text, local: reply.local, demo: reply.demo }]
    // What was said, and what went up on the screen, kept for the person who
    // said it - so the second time this ground comes round they know it is
    // the second time. Their own words only; the round the supervisor reads
    // never sees this (talkLog.ts).
    talkLog.settle(reply.run !== undefined && who.toolId ? 'ran' : 'answered', reply.text, reply.demo ?? '')
    // With a scene up, what is SAID is the sentence plus the scene's own two
    // lines - what to watch for, and which control to reach for. They are on
    // the card either way; a technician who put a simulation on the screen
    // and then went quiet, leaving the student to read the instructions off
    // it, is not how anybody explains anything at a tool. The lines are
    // authored per scene in the catalogue, so this is still only reading out
    // what the repo wrote (demos.ts).
    perform(reply.text, who.body, reply.demo ? spokenDemoLines(reply.demo) : '')
    notify()
    // Then, if they said they would run it, they run it. Separate from the
    // sentence because it takes real seconds of solver time: the student
    // reads what she said, and hears it, while the tool works.
    //
    // `busy` normally stays up throughout - the ask box belongs to the
    // technician until the run comes back. The ONE exception is a line that
    // asked them to call it first: then the box goes back to the student
    // while the tool works, and the report waits on what they type. See
    // predict.ts.
    if (reply.run !== undefined && who.toolId) {
      if (reply.ask) {
        hold(who, reply.ask)
        busy = false
      }
      notify()
      const called = await execute(who, reply.run || question)
      // They called it and the tool has answered. One more line from her, and
      // it is a question rather than an answer. See `askWhy`.
      if (called) await askWhy(who, called.about)
    }
    if (open !== who) return
    busy = false
    notify()
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
